'use client';

import { useRef, type PointerEvent } from 'react';

type Rect = { x1: number; y1: number; x2: number; y2: number };

type Props = {
  /** Backdrop image (the node's output, or an upstream image) for context. */
  image?: string;
  /** Fallback aspect ratio (w/h) when there's no image yet. */
  aspect: number;
} & (
  | { mode: 'point'; x: number; y: number; onChange: (x: number, y: number) => void }
  | { mode: 'rect'; rect: Rect; onChange: (r: Rect) => void }
);

/**
 * Drag-to-place pad for the draw nodes: drag a point (Text / Composite position) or sweep a
 * rect (Shape bounds) over the node's image. Normalized 0..1 coords. Window pointer listeners
 * + nodrag/nopan keep the drag robust and stop React Flow from panning the canvas.
 */
export function PlacementPad(props: Props) {
  const ref = useRef<HTMLDivElement>(null);

  const norm = (cx: number, cy: number) => {
    const b = ref.current!.getBoundingClientRect();
    return {
      x: Math.min(1, Math.max(0, (cx - b.left) / b.width)),
      y: Math.min(1, Math.max(0, (cy - b.top) / b.height)),
    };
  };

  const onDown = (e: PointerEvent) => {
    if (!ref.current) return;
    e.preventDefault();
    e.stopPropagation();
    const start = norm(e.clientX, e.clientY);
    if (props.mode === 'point') props.onChange(start.x, start.y);
    const move = (ev: globalThis.PointerEvent) => {
      const p = norm(ev.clientX, ev.clientY);
      if (props.mode === 'point') props.onChange(p.x, p.y);
      else props.onChange({ x1: start.x, y1: start.y, x2: p.x, y2: p.y });
    };
    const up = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  };

  return (
    <div
      ref={ref}
      onPointerDown={onDown}
      title="Drag to place"
      className="relative nodrag nopan select-none touch-none rounded-lg overflow-hidden cursor-crosshair bg-[var(--bg-elevated)] border border-[var(--border-subtle)]"
      style={props.image ? undefined : { aspectRatio: String(props.aspect || 1) }}
    >
      {props.image && (
        // eslint-disable-next-line @next/next/no-img-element
        <img src={props.image} alt="" className="w-full block pointer-events-none" draggable={false} />
      )}
      {props.mode === 'point' ? (
        <div
          className="absolute w-3 h-3 -ml-1.5 -mt-1.5 rounded-full bg-[var(--accent)] ring-2 ring-white/80 pointer-events-none"
          style={{ left: `${props.x * 100}%`, top: `${props.y * 100}%` }}
        />
      ) : (
        <div
          className="absolute border-2 border-[var(--accent)] bg-[var(--accent-soft)] pointer-events-none"
          style={{
            left: `${Math.min(props.rect.x1, props.rect.x2) * 100}%`,
            top: `${Math.min(props.rect.y1, props.rect.y2) * 100}%`,
            width: `${Math.abs(props.rect.x2 - props.rect.x1) * 100}%`,
            height: `${Math.abs(props.rect.y2 - props.rect.y1) * 100}%`,
          }}
        />
      )}
    </div>
  );
}
